import moment from 'moment';
import { parseMoment } from '../../services/parseMoment';

export const DATE_CONSTRAINT_NONE = 'none';
export const DATE_CONSTRAINT_BIRTHDAY = 'birthday';
export const DATE_CONSTRAINT_FUTURE = 'future';
export const DATE_CONSTRAINT_PAST = 'past';
export const DATE_CONSTRAINT_CUSTOM = 'custom';
export const DATE_CONSTRAINT_PHASE = 'phase';

export const DATE_CONSTRAINT_VALUES = [
    DATE_CONSTRAINT_BIRTHDAY,
    DATE_CONSTRAINT_PAST,
    DATE_CONSTRAINT_FUTURE,
    DATE_CONSTRAINT_CUSTOM,
    DATE_CONSTRAINT_PHASE,
];

const BIRTHDAY_MAX_AGE_YEARS = 120;
const DEFAULT_OPEN_AT_YEARS_AGO = 30;

export function phaseEnvelopeBounds(phases, withTime) {
    if (!phases?.length)
        return {};

    const starts = phases.map(p => parseMoment(p.start_at)).filter(Boolean);
    const stops = phases.map(p => parseMoment(p.stop_at)).filter(Boolean);
    if (!starts.length || !stops.length)
        return {};

    const minMoment = moment.min(starts).startOf('day');
    const maxMoment = moment.max(stops).endOf('day');

    if (withTime) {
        return {
            min: minMoment.format('YYYY-MM-DDTHH:mm'),
            max: maxMoment.format('YYYY-MM-DDTHH:mm'),
        };
    }

    return {
        min: minMoment.format('YYYY-MM-DD'),
        max: maxMoment.format('YYYY-MM-DD'),
    };
}

export function hasPhaseEnvelopeBounds(phases) {
    const bounds = phaseEnvelopeBounds(phases, false);
    return !!(bounds.min && bounds.max);
}

export function birthdayDatePickerProps(openAtYearsAgo = DEFAULT_OPEN_AT_YEARS_AGO) {
    return {
        timeFormat: false,
        max: moment().format('YYYY-MM-DD'),
        min: moment().subtract(BIRTHDAY_MAX_AGE_YEARS, 'years').format('YYYY-MM-DD'),
        openAtYearsAgo,
        compact: true,
    };
}

function todayBound(withTime, direction) {
    const now = moment();
    if (withTime) {
        return direction === 'min'
            ? now.format('YYYY-MM-DDTHH:mm')
            : now.format('YYYY-MM-DDTHH:mm');
    }
    return now.format('YYYY-MM-DD');
}

function formatCustomBound(value, withTime) {
    if (!value)
        return undefined;

    const parsed = parseMoment(value);
    if (!parsed)
        return undefined;

    return withTime
        ? parsed.format('YYYY-MM-DDTHH:mm')
        : parsed.format('YYYY-MM-DD');
}

/**
 * Resolve DateTimePicker props from a form layout datetime field configuration.
 *
 * @param {Object} options
 * @param {string|undefined} options.constraint - explicit date_constraint from field.content
 * @param {boolean} options.withTime
 * @param {Object} options.content - field.content
 * @param {Array} options.phases - context.resources.phases
 * @param {string} options.fieldName - field name for legacy birthday detection
 */
export function resolveDatePickerProps({
    constraint,
    withTime,
    content = {},
    phases,
    fieldName,
}) {
    const explicitConstraint = constraint ?? content?.date_constraint;
    const phaseBounds = phaseEnvelopeBounds(phases, withTime);

    if (!explicitConstraint) {
        const isBirthdate = fieldName === 'birthday' || fieldName === 'birthdate';
        if (isBirthdate) {
            const openAtYearsAgo = content?.open_at_years_ago ?? DEFAULT_OPEN_AT_YEARS_AGO;
            return {
                timeFormat: false,
                ...birthdayDatePickerProps(openAtYearsAgo),
            };
        }

        return {
            timeFormat: withTime,
            min: phaseBounds.min,
            max: phaseBounds.max,
        };
    }

    switch (explicitConstraint) {
        case DATE_CONSTRAINT_NONE:
            return { timeFormat: withTime };

        case DATE_CONSTRAINT_BIRTHDAY: {
            const openAtYearsAgo = content?.open_at_years_ago ?? DEFAULT_OPEN_AT_YEARS_AGO;
            return {
                timeFormat: false,
                ...birthdayDatePickerProps(openAtYearsAgo),
            };
        }

        case DATE_CONSTRAINT_FUTURE:
            return {
                timeFormat: withTime,
                min: todayBound(withTime, 'min'),
            };

        case DATE_CONSTRAINT_PAST:
            return {
                timeFormat: withTime,
                max: todayBound(withTime, 'max'),
            };

        case DATE_CONSTRAINT_CUSTOM:
            return {
                timeFormat: withTime,
                min: formatCustomBound(content?.date_min, withTime),
                max: formatCustomBound(content?.date_max, withTime),
            };

        case DATE_CONSTRAINT_PHASE:
            return {
                timeFormat: withTime,
                min: phaseBounds.min,
                max: phaseBounds.max,
            };

        default:
            return {
                timeFormat: withTime,
                min: phaseBounds.min,
                max: phaseBounds.max,
            };
    }
}
